import React, { useEffect, useMemo, useState } from 'react';
import {
    MenuItem,
    Grid,
    Box,
    InputBase,
    Button,
    CircularProgress,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Alert,
    Paper,
    Tab,
    Tabs,
} from '@mui/material';
import Section from './Common/Section';
import { useParams, useNavigate } from 'react-router-dom';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { hazardRiskScore } from '../constants/hazardConstants';
import Image from 'mui-image';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import FileUploadModal from './Modal/FileUploadModal';
import fileUploadService from '../api/fileUploadService';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import RiskControlModal from './Modal/RiskControlModal';
import DeleteIcon from '@mui/icons-material/Delete';
import OrgApiService from '../api/OrgApiService';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    AutoLink,
    BlockQuote,
    Bold,
    ClassicEditor,
    Essentials, FontFamily, FontSize,
    Heading,
    Indent,
    Italic,
    Link, LinkEditing, LinkUI,
    List, MediaEmbed,
    Mention,
    Paragraph, Table,
    Undo,
} from 'ckeditor5';
import HazardApiService from '../api/HazardApiService';
import LocationApiService from '../api/LocationApiService';
import RiskSourceApiService from '../api/RiskSourceApiService';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from './Utils/AuthUtil';
import { useSnackbar } from './Common/SnackbarProvider';

// Stable tokens for risk levels — kept separate from translated display so the
// internal threshold comparisons / CSS colors aren't affected by locale.
const carouselArrowSx = (side) => ({
    backgroundColor: 'white',
    position: 'absolute',
    top: '50%',
    [side]: 0,
    opacity: 0.5,
});

const RISK_LEVEL_KEY = {
    LOW: 'riskLevel.low',
    MEDIUM: 'riskLevel.medium',
    HIGH: 'riskLevel.high',
    VERY_HIGH: 'riskLevel.veryHigh',
};

const RISK_DESC_LABEL_KEY = {
    Environmental: 'hazardPage.riskDescLabel.environmental',
    Quality: 'hazardPage.riskDescLabel.quality',
    OHS: 'hazardPage.riskDescLabel.ohs',
    'Integrated/Multiple': 'hazardPage.riskDescLabel.integrated',
    Other: 'hazardPage.riskDescLabel.other',
    Energy: 'hazardPage.riskDescLabel.energy',
    CyberSecurity: 'hazardPage.riskDescLabel.cyberSecurity',
};

const Hazard = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const [hazardId, setHazardId] = useState(params.hazardId);
    const [currentHazard, setCurrentHazard] = useState(undefined);
    const [newHazard, setNewHazard] = useState({});
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState({ projectId: 0, projectName: '' });
    const [department, setDepartment] = useState('');
    const [process, setProcess] = useState('');
    const [job, setJob] = useState('');
    const [task, setTask] = useState('');
    const [equipment, setEquipment] = useState('');
    const [riskDescription, setRiskDescription] = useState('');
    const [selectedRiskSource, setSelectedRiskSource] = useState('');
    const [riskSources, setRiskSources] = useState([]);
    const [severity, setSeverity] = useState({ svId: 0, svName: '', svFactor: 1 });
    const [likelihood, setLikelihood] = useState({ lkId: 0, lkName: '', lkFactor: 1 });
    const [riskScoreUncontrolled, setRiskScoreUncontrolled] = useState({ riskScore: 0 });
    const [obligation, setObligation] = useState('');
    const [riskScoreControlled, setRiskScoreControlled] = useState({ riskScore: 0 });
    const [controls, setControls] = useState([]);
    const [likelihoods, setLikelihoods] = useState([]);
    const [severities, setSeverities] = useState([]);
    const [currentImage, setCurrentImage] = useState(0);
    const [images, setImages] = useState([]);
    const [files, setFiles] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showImageDeleteDialog, setShowImageDeleteDialog] = useState(false);
    const [showFileDeleteDialog, setShowFileDeleteDialog] = useState(false);
    const [currentFile, setCurrentFile] = useState({});
    const [controlTypes, setControlTypes] = useState([]);
    const [showAddControlModal, setShowAddControlModal] = useState(false);
    const [currentControl, setCurrentControl] = useState({});
    const [showControlDeleteDialog, setShowControlDeleteDialog] = useState(false);
    const [hazardAudits, setHazardAudits] = useState([]);
    const [departmentAutocomplete, setDepartmentAutocomplete] = useState(['']);
    const [processAutocomplete, setProcessAutocomplete] = useState(['']);
    const [jobAutocomplete, setJobAutocomplete] = useState(['']);
    const [taskAutocomplete, setTaskAutocomplete] = useState(['']);
    const [equipmentAutocomplete, setEquipmentAutocomplete] = useState(['']);
    // eslint-disable-next-line no-unused-vars
    const [title, setTitle] = useState(t('hazardPage.title.create'));
    const [notes, setNotes] = useState('');
    const [changes, setChanges] = useState([]);
    const [isSignificant, setIsSignificant] = useState(false);
    const [showRiskChanges, setShowRiskChanges] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
    const [analysisError, setAnalysisError] = useState('');
    const [previousAnalysisResults, setPreviousAnalysisResults] = useState([]);
    const [tabValue, setTabValue] = useState(0);

    const orgApiService = new OrgApiService();
    const hazardApiService = new HazardApiService();
    const locationApiService = new LocationApiService();
    const riskSourceApiService = new RiskSourceApiService();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const canChangeHazard = useMemo(() => AuthUtil.canChangeHazards(userToken), [userToken]);
    const userId = useMemo(() => AuthUtil.getUserId(userToken), [userToken]);
    const isSuperAdmin = useMemo(() => AuthUtil.isSuperAdmin(userToken), [userToken]);

    const handleLoad = () => {
        setLoading(false);
    };

    const handlePrev = (event) => {
        event.preventDefault();

        if (images.length > 1) {
            setCurrentImage((currentImage - 1 + images.length) % images.length);
            setLoading(true);
        }
    };

    const handleNext = (event) => {
        event.preventDefault();

        if (images.length > 1) {
            setCurrentImage((currentImage + 1) % images.length);
            setLoading(true);
        }
    };

    const handleAnalyzeImage = async () => {
        if (images.length === 0) return;

        const currentAttachment = images[currentImage];
        setAnalyzing(true);
        setAnalysisError('');
        setAnalysisResult(null);

        try {
            const response = await hazardApiService.analyzeHazardImage(hazardId, currentAttachment.attachmentLinkId);

            if (response.success) {
                setAnalysisResult(response.data);
                setShowAnalysisDialog(true);
                reloadAnalysisResults();
            } else {
                setAnalysisError(response.message || t('hazardPage.toast.analysisFailed'));
            }
        } catch (err) {
            setAnalysisError(t('hazardPage.toast.analyzeImageFailed'));
        } finally {
            setAnalyzing(false);
        }
    };

    const handleAddAsControl = async (risk, analysisResultId) => {
        if (controlTypes.length === 0) return;

        const defaultControlType = controlTypes[0];
        const response = await hazardApiService.addHazardControl({
            HcHazard: hazardId,
            HcDescription: t('hazardPage.aiAnalysis.recommendationFormat', { risk: risk.risk, recommendation: risk.recommendation }),
            HcControlType: defaultControlType.ctId,
        });

        if (response.success) {
            // Dismiss the analysis result since it's been added as a control
            if (analysisResultId) {
                await hazardApiService.dismissAnalysisResult(analysisResultId);
                setPreviousAnalysisResults(prev => prev.filter(r => r.id !== analysisResultId));
            }

            // Remove from current analysis dialog results
            if (analysisResult) {
                setAnalysisResult(prev => ({
                    ...prev,
                    identifiedRisks: prev.identifiedRisks.filter(r => r.risk !== risk.risk),
                }));
            }

            reloadControls();
            snackbar.success(t('hazardPage.toast.controlAdded'));
        }
    };

    const handleDismissResult = async (id) => {
        const response = await hazardApiService.dismissAnalysisResult(id);
        if (response.success) {
            // Remove from visible list — still exists in DB as dismissed
            setPreviousAnalysisResults(prev => prev.filter(r => r.id !== id));
        }
    };

    const reloadAnalysisResults = () => {
        hazardApiService.getAnalysisResults(hazardId).then((response) => {
            if (response.success) {
                setPreviousAnalysisResults(response.data);
            }
        });
    };

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return 'error';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'success';
            default: return 'default';
        }
    };

    const controlsColumns = useMemo(() => [
        {
            field: 'hcDescription',
            headerName: t('fieldName.description'),
            flex: 3,
            renderCell: (params) => {
                return params.row.controlLink ? <a target={'_blank'} href={`${params.row.controlLink}`} rel="noreferrer">{params.row.hcDescription}</a> : `${params.row.hcDescription}`;
            }
        },
        {
            field: 'ctName',
            headerName: t('common.type'),
            flex: 2,
            valueGetter: (params) => params.row.hcControlTypeNavigation.localizedName ?? params.row.hcControlTypeNavigation.ctName,
    }, {
        field: 'ctFactor',
        headerName: t('hazardPage.controls.column.factor'),
        flex: 1,
        valueGetter: (params) => params.row.hcControlTypeNavigation.ctFactor,
    }, {
        field: 'delete',
        headerName: '',
        flex: 1,
        renderCell: (params) => (<DeleteIcon
            sx={{ cursor: 'pointer' }}
            onClick={() => onControlDeleteClick(params.row)}
        />),
    }], [t]);

    const fileColumns = useMemo(() => [{
        field: 'filename',
        headerName: t('common.filename'),
        flex: 1,
        renderCell: (params) => {
            const handleDownloadClick = async (event, row) => {
                event.preventDefault();
                const response = await fileUploadService.downloadHazardAttachment(row, hazardId);

                // Create a blob object URL for the downloaded file
                const blob = new Blob([response.data], { type: row.contentType });
                const url = URL.createObjectURL(blob);

                // Create a temporary anchor tag and click it to trigger the download
                const link = document.createElement('a');
                link.href = url;
                link.download = row.filename;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            };

            return (<a href={`Hazard/${hazardId}`} target='_blank'
                onClick={(event) => handleDownloadClick(event, params.row)} rel="noreferrer">
                {params.value}
            </a>);
        },
    }, { field: 'description', headerName: t('fieldName.description'), flex: 2 }, {
        field: 'delete',
        headerName: t('common.delete'),
        flex: 1,
        renderCell: (params) => (<DeleteIcon
            sx={{ cursor: 'pointer' }}
            onClick={() => handleDeleteFile(params.row)}
        />),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }], [t]);

    function handleDeleteFile (file) {
        setCurrentFile(file);
        setShowFileDeleteDialog(true);
    }

    const onFileDeleteClick = (deleteValue) => {
        if (deleteValue) {
            fileUploadService.deleteHazardAttachment(currentFile.attachmentLinkId).then(() => {
                window.location.href = '/Hazard/' + hazardId;
            });
        }

        setShowFileDeleteDialog(false);
    };

    function onControlDeleteClick (control) {
        setCurrentControl(control);
        setShowControlDeleteDialog(true);
    }

    const handleDeleteControl = async (deleteValue) => {
        if (deleteValue) {
            if (currentControl.hcId > 0) {
                await hazardApiService.deleteHazardControl(currentControl.hcId);
                reloadControls();
            }
        }

        setShowControlDeleteDialog(false);
    };

    const hazardAuditColumns = useMemo(() => [{
        field: 'updatedDate',
        headerName: t('hazardPage.audits.column.dateUpdated'),
        flex: 1,
    }, { field: 'lastUpdatedUser', headerName: t('hazardPage.audits.column.updatedBy'), flex: 1 }, {
        field: 'displayChanges',
        headerName: t('hazardPage.audits.column.changes'),
        renderCell: (params) => (<Box sx={{ whiteSpace: 'pre-line' }}>
            {params.value}
        </Box>),
        flex: 6,
    }], [t]);

    function handleFileUpload (event) {
        if (event) {
            fileUploadService.uploadHazardAttachment(event, hazardId).then(() => {
                window.location.href = '/Hazard/' + hazardId;
            });
        }

        setShowUploadModal(false);
    }

    function handleAddControlModalClose () {
        setShowAddControlModal(false);
        reloadControls();
    }

    function reloadControls () {
        hazardApiService.getHazardData(hazardId).then((hazardResponse) => {
            const hazardObject = hazardResponse.data;

            if (!hazardObject) {
                return;
            }

            setControls(hazardObject.hazardControlDtos);
            calculateRiskScores(severity.svFactor, likelihood.lkFactor, hazardObject.hazardControlDtos);
        });
    }

    const onImageDeleteClick = (deleteValue) => {
        if (deleteValue) {
            fileUploadService.deleteHazardAttachment(images[currentImage].attachmentLinkId).then(() => {
                images.splice(currentImage, 1);
                if (currentImage > 0) {
                    setCurrentImage(currentImage - 1);
                }
                else {
                    setCurrentImage(0);
                }
            });
        }

        setShowImageDeleteDialog(false);
    };

    function handleFileDelete () {
        setShowImageDeleteDialog(true);
    }

    async function loadHazard() {
        setLoading(true);
        const riskSourcesResponse = await riskSourceApiService.getRiskSources();

        if(riskSourcesResponse.data) {
            setRiskSources(riskSourcesResponse.data);
        }

        if(isSuperAdmin) {
            const orgs = await orgApiService.getSelectableOrgs();

            if(orgs.success) {
                setOrganizations(orgs.data);
            }
        } else {
            const org = await orgApiService.getUsersOrg(userId);

            if(org.success) {
                let orgsEmptyArray = [];
                orgsEmptyArray.push(org.data);
                setOrganizations(orgsEmptyArray);
                setSelectedOrg(org.data.ogName);
                updateProjects(org.data.ogId);
            }
        }

        // Get all the Control Types to utilize for editing a risk control
        hazardApiService.getHazardControlTypes().then((controlTypesResponse) => {
            setControlTypes(controlTypesResponse.data);
        });

        // If the hazardId is 0, then we are going to be creating the Risk which means we can't get risk data.
        if (isHazardCreated) {
            setTitle(t('hazardPage.title.edit', { id: hazardId }));

            // Get the audits for this hazard, which include both hazard audits and hazard control audits
            hazardApiService.getHazardAudits(hazardId).then((auditResponse) => {
                setHazardAudits(auditResponse.data);
            });

            // Load previous AI analysis results
            hazardApiService.getAnalysisResults(hazardId).then((analysisResponse) => {
                if (analysisResponse.success) {
                    setPreviousAnalysisResults(analysisResponse.data);
                }
            });

            hazardApiService.getHazardData(hazardId).then(async (hazardResponse) => {
                const hazardObject = hazardResponse.data;

                if (!hazardObject) {
                    return;
                }
                const { likelihoodList, severityList } = await updateLikelihoodAndSeverities();
                loadAutoCompleteFields(hazardObject.hzProjectDto.pjId);

                setCurrentHazard(hazardObject);
                setNewHazard(hazardObject);

                setSelectedOrg(hazardObject.hzProjectDto.pjOrgDto.ogName);
                setSelectedProject({
                    projectId: hazardObject.hzProjectDto.pjId, projectName: hazardObject.hzProjectDto.pjName,
                });
                setDepartment(hazardObject.hzDepartmentName);
                setProcess(hazardObject.hzProcess);
                setJob(hazardObject.hzJob);
                setTask(hazardObject.hzTask);
                setEquipment(hazardObject.hzEquipmentId);
                setSelectedRiskSource(hazardObject.hzSystemAreaDto?.name);
                setObligation(hazardObject.hzObligation);
                setNotes(hazardObject.hzNotes);
                setIsSignificant(hazardObject.significant);

                setRiskDescription(hazardObject.hzRiskDescription ? hazardObject.hzRiskDescription : '');

                setControls(hazardObject.hazardControlDtos);

                let attachmentIndex = 0;
                const tempImages = [];
                const tempFiles = [];

                // Get all the hazard attachments to display
                hazardObject.hazardAttachmentLinkDtos.forEach((attachment) => {
                    attachmentIndex = attachmentIndex + 1;

                    const curAttachment = {
                        id: attachmentIndex,
                        url: attachment.url,
                        blobUrl: attachment.blobUrl,
                        description: attachment.description,
                        alt: 'Attachment ' + attachmentIndex,
                        attachmentLinkId: attachment.attachmentLinkId,
                        filename: attachment.filename,
                        contentType: attachment.contentType,
                    };

                    if (attachment.contentType.includes('image')) {
                        tempImages.push(curAttachment);
                    } else {
                        tempFiles.push(curAttachment);
                    }
                });

                setImages(tempImages);
                setFiles(tempFiles);

                // Find and set the likelihood for the current Hazard from the likelihoods API call we just did
                const hazardLikelihood = likelihoodList.find(likely => likely.lkId === hazardObject.hzLikelihood);
                setLikelihood({
                    lkId: hazardLikelihood?.lkId,
                    lkName: hazardLikelihood?.lkName,
                    lkFactor: hazardLikelihood?.lkFactor,
                });

                const hazardSeverity = severityList.find(sev => sev.svId === hazardObject.hzSeverity);
                setSeverity({
                    svId: hazardSeverity.svId,
                    svName: hazardSeverity.svName,
                    svFactor: hazardSeverity.svFactor,
                });

                if (hazardSeverity && hazardLikelihood) {
                    // Do some risk score calculations
                    calculateRiskScores(hazardSeverity.svFactor, hazardLikelihood.lkFactor, hazardObject?.hazardControlDtos);
                }

                // Update the list of projects based on the organization
                updateProjects(hazardObject.hzProjectDto.pjOrg);
            })
                .catch((e) => {
                    navigate('/Hazards');
                    snackbar.error(t('hazardPage.toast.loadError'));
                });
        }
        else {
            void updateLikelihoodAndSeverities();
            calculateRiskScores(severity.svFactor, likelihood.lkFactor);
            setTitle(t('hazardPage.title.create'));
        }
    }

    useEffect(() => {
        void loadHazard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hazardId]);

    async function loadAutoCompleteFields(projectId) {
        hazardApiService.getAutoCompleteDepartments(projectId).then((departmentOptions) => {
            setDepartmentAutocomplete(departmentOptions.data);
        });

        hazardApiService.getAutoCompleteProcesses(projectId).then((processOptions) => {
            setProcessAutocomplete(processOptions.data);
        });

        hazardApiService.getAutoCompleteJobs(projectId).then((jobOptions) => {
            setJobAutocomplete(jobOptions.data);
        });

        hazardApiService.getAutoCompleteTasks(projectId).then((taskOptions) => {
            setTaskAutocomplete(taskOptions.data);
        });

        hazardApiService.getAutoCompleteEquipment(projectId).then((equipmentOptions) => {
            setEquipmentAutocomplete(equipmentOptions.data);
        });
    }

    async function updateLikelihoodAndSeverities() {
        // Get all the likelihoods
        const likelihoodResponse = await hazardApiService.getAllLikelihoods();

        let likelihoodList = [];

        // Create an empty likelihood
        likelihoodList.push({
            lkId: 0, lkName: '\u00A0', lkFactor: 1,
        });

        likelihoodList = likelihoodList.concat(likelihoodResponse.data);

        // Set the likelihoods and push in the empty element
        setLikelihoods(likelihoodList);

        // Get all the severities
        const severityResponse = await hazardApiService.getAllSeverities();

        let severityList = [];

        severityList.push({
            svId: 0, svName: '\u00A0', svFactor: 1,
        });

        severityList = severityList.concat(severityResponse.data);

        setSeverities(severityList);

        return {likelihoodList, severityList};
    }

    function calculateRiskScores (severity, likelihood, controls) {
        const riskScore = (severity * likelihood).toFixed(2);
        setRiskScoreUncontrolled({ riskScore });
        setRiskScoreControlled({ riskScore: getRiskScoreControlled(controls, riskScore) });
    }

    function getRiskScoreControlled (factors, riskScore) {
        riskScore = parseFloat(riskScore);

        factors?.forEach(hazardControl => {
            const controlFactor = hazardControl.hcControlTypeNavigation.ctFactor;
            riskScore = riskScore * controlFactor;
        });

        return riskScore.toFixed(2);
    }

    function cancelChanges () {
        navigate('/Hazards');
    }

    function createNewRisk () {
        window.location.href = '/Hazard';
    }

    async function onSave () {
        const updatedHazard = getUpdatedHazard();

        if (!isHazardCreated) {
            const temp = await hazardApiService.addNewHazard(updatedHazard);

            navigate('/Hazard/' + temp.data.hzId);
            snackbar.success(t('hazardPage.toast.added'));
            setHazardId(temp.data.hzId);
        } else if (!disableSaveButton) {
            const updatedHazardResponse = await hazardApiService.updateHazard(updatedHazard);
            if(updatedHazardResponse.success) {
                snackbar.success(t('hazardPage.toast.saved'));
                navigate('/Hazard/' + hazardId, { replace: true });
                setChanges([]);
            } else {
                snackbar.error(t('hazardPage.toast.updateError'));
            }
        }
    }

    function getUpdatedHazard () {
        const formInputs = {
            hzRiskDescription: riskDescription,
            hzDepartmentName: department,
            hzEquipmentId: equipment,
            hzJob: job,
            hzLikelihood: likelihood.lkId,
            hzObligation: obligation,
            hzProcess: process,
            hzProject: selectedProject.projectId,
            hzSeverity: severity.svId,
            hzTask: task,
            hzSystemArea: riskSources.find(riskSource => riskSource.name === selectedRiskSource)?.id,
            hzNotes: notes,
            significant: isSignificant === 'true',
        };

        return { ...newHazard, ...formInputs };
    }

    const isHazardCreated = useMemo(() => hazardId !== '0' && hazardId !== undefined, [hazardId]);
    const changesLength = Object.keys(changes).length;
    const disableSaveButton = useMemo(() => (isHazardCreated && changesLength === 0) && canChangeHazard,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [changesLength, canChangeHazard]);

    const riskScoreInfo = useMemo(
        () => getRiskScoreValue(riskScoreControlled.riskScore),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [riskScoreControlled.riskScore, t]
    );

    function handleAddAttachment () {
        if (isHazardCreated) {
            setShowUploadModal(true);
        }
    }

    function handleAddControl () {
        if (isHazardCreated) {
            setShowAddControlModal(true);
        }
    }

    function getRiskScoreValue (value) {
        let color, levelKey;
        if (value < hazardRiskScore.LOW) { color = '#9ACD32'; levelKey = RISK_LEVEL_KEY.LOW; }
        else if (value < hazardRiskScore.MEDIUM) { color = '#FFFF00'; levelKey = RISK_LEVEL_KEY.MEDIUM; }
        else if (value < hazardRiskScore.HIGH) { color = '#FFA500'; levelKey = RISK_LEVEL_KEY.HIGH; }
        else { color = '#FF0000'; levelKey = RISK_LEVEL_KEY.VERY_HIGH; }
        return {
            color,
            text: t('hazardPage.riskScoreLabel', { score: value, level: t(levelKey) }),
        };
    }

    const updateProjects = (orgId) => {
        if (orgId) {
            locationApiService.getLocationsForOrg(orgId).then((projectResponse) => {
                const projectsFound = projectResponse.data.projects;

                if (projectsFound) {
                    setProjects(projectsFound);
                }
            });
        }
    };

    function handleProjectChange(event) {
        const projectId = projects.find(proj => proj.projectName === event.target.value)?.projectId;

        setSelectedProject({
            projectId: projectId,
            projectName: event.target.value,
        });

        const newValue = event.target.value;
        if(currentHazard?.hzProjectDto.pjName !== newValue) {
            setChanges({
                ...changes,
                'Project': newValue,
            });
        } else {
            removeChange('Project');
        }
        loadAutoCompleteFields(projectId);
    }

    function handleIsSignificantChange(event) {
        const newValue = event.target.value;
        setIsSignificant(newValue);

        if(currentHazard?.significant !== newValue) {
            setChanges({
                ...changes,
                'significant': newValue === 'true',
            });
        } else {
            removeChange('significant');
        }
    }

    function removeChange (objectToRemove) {
        const tempChangeArray = changes;
        delete tempChangeArray[objectToRemove];
        setChanges(tempChangeArray);
    }

    function getRiskDescriptionLabel() {
        const key = RISK_DESC_LABEL_KEY[selectedRiskSource];
        return key ? t(key) : t('fieldName.riskDescription');
    }

    return (
        <Box className="edit-risk-container">
            <form className="edit-risk-form">
                {/* Sticky header bar */}
                <Paper
                    elevation={2}
                    sx={{
                        position: 'sticky',
                        // Sticks directly beneath the app header, whose height varies with the impersonation banner.
                        top: 'var(--ct-header-h, 56px)',
                        zIndex: 2,
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isHazardCreated
                            ? t('hazardPage.title.edit', { id: hazardId })
                            : t('hazardPage.title.new')}
                    </Typography>
                    <Chip
                        label={riskScoreInfo.text}
                        size="small"
                        sx={{
                            backgroundColor: riskScoreInfo.color,
                            color: 'black',
                            fontWeight: 600,
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={createNewRisk}
                    >
                        {t('hazardsPage.createNewRisk')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={cancelChanges}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={disableSaveButton}
                        onClick={onSave}
                    >
                        {t('common.save')}
                    </Button>
                </Paper>

                {/* Main two-column grid */}
                <Grid container spacing={2} sx={{ px: 1 }}>
                    {/* Left column */}
                    <Grid item xs={12} md={8}>
                        <Section title={t('hazardPage.section.identity')}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('fieldName.organization')}
                                    disabled={!isSuperAdmin}
                                    value={organizations.length > 0 ? selectedOrg : ''}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.hzProjectDto.pjOrgDto.ogName !== newValue) {
                                            setChanges({
                                                ...changes,
                                                'Org': newValue,
                                            });
                                        } else {
                                            removeChange('Org');
                                        }
                                        setSelectedOrg(event.target.value);
                                        updateProjects(organizations.find(org => org.ogName === event.target.value)?.ogId);

                                        // Since the organization was just changed, we shouldn't have anything selected for project.
                                        setSelectedProject({ projectId: 0, projectName: '' });
                                    }}
                                >
                                    {organizations.map((org) => (
                                        <MenuItem key={org.ogId} value={org.ogName}>
                                            {org.ogName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('hazardPage.field.projectsLocation')}
                                    disabled={!canChangeHazard}
                                    value={projects.length > 0 ? selectedProject.projectName : ''}
                                    onChange={handleProjectChange}
                                >
                                    {projects.map((project) => (
                                        <MenuItem key={project.projectId} value={project.projectName}>
                                            {project.projectName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={departmentAutocomplete}
                                    disabled={!canChangeHazard}
                                    value={department}
                                    onInputChange={(event, value) => {
                                        if(currentHazard?.hzDepartmentName !== value) {
                                            setChanges({
                                                ...changes,
                                                'Department': value,
                                            });
                                        } else {
                                            removeChange('Department');
                                        }
                                        setDepartment(value);
                                    }}
                                    getOptionLabel={(option) => option}
                                    freeSolo={true}
                                    renderInput={(input) => (
                                        <TextField {...input} fullWidth label={t('fieldName.department')}/>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    disabled={!canChangeHazard}
                                    options={processAutocomplete}
                                    value={process}
                                    onInputChange={(event, value) => {
                                        if(currentHazard?.hzProcess !== value) {
                                            setChanges({
                                                ...changes,
                                                'Process': value,
                                            });
                                        } else {
                                            removeChange('Process');
                                        }
                                        setProcess(value);
                                    }}
                                    getOptionLabel={(option) => option}
                                    freeSolo={true}
                                    renderInput={(input) => (
                                        <TextField {...input} fullWidth label={t('fieldName.process')}/>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    disabled={!canChangeHazard}
                                    options={jobAutocomplete}
                                    value={job}
                                    onInputChange={(event, value) => {
                                        if(currentHazard?.hzJob !== value) {
                                            setChanges({
                                                ...changes,
                                                'Job': value,
                                            });
                                        } else {
                                            removeChange('Job');
                                        }
                                        setJob(value);
                                    }}
                                    getOptionLabel={(option) => option}
                                    freeSolo={true}
                                    renderInput={(input) => (
                                        <TextField {...input} fullWidth label={t('fieldName.job')}/>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    disabled={!canChangeHazard}
                                    options={taskAutocomplete}
                                    value={task}
                                    onInputChange={(event, value) => {
                                        if(currentHazard?.hzTask !== value) {
                                            setChanges({
                                                ...changes,
                                                'Task': value,
                                            });
                                        } else {
                                            removeChange('Task');
                                        }
                                        setTask(value);
                                    }}
                                    getOptionLabel={(option) => option}
                                    freeSolo={true}
                                    renderInput={(input) => (
                                        <TextField {...input} fullWidth label={t('fieldName.task')}/>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    disabled={!canChangeHazard}
                                    options={equipmentAutocomplete}
                                    value={equipment}
                                    onInputChange={(event, value) => {
                                        if(currentHazard?.hzEquipmentId !== value) {
                                            setChanges({
                                                ...changes,
                                                'Equipment': value,
                                            });
                                        } else {
                                            removeChange('Equipment');
                                        }
                                        setEquipment(value);
                                    }}
                                    getOptionLabel={(option) => option}
                                    freeSolo={true}
                                    renderInput={(input) => (
                                        <TextField {...input} fullWidth label={t('fieldName.equipment')}/>
                                    )}
                                />
                            </Grid>
                        </Section>

                        <Section title={t('hazardPage.section.classification')}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('fieldName.riskSource')}
                                    disabled={!canChangeHazard}
                                    value={selectedRiskSource}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.hzSystemAreaDto.name !== newValue) {
                                            setChanges({
                                                ...changes,
                                                'RiskSource': newValue,
                                            });
                                        } else {
                                            removeChange('RiskSource');
                                        }
                                        setSelectedRiskSource(newValue);
                                    }}
                                >
                                    {riskSources.map((riskSource) => (
                                        <MenuItem key={riskSource.id} value={riskSource.name}>
                                            {riskSource.localizedName ?? riskSource.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('fieldName.significant')}
                                    disabled={!canChangeHazard}
                                    value={String(isSignificant)}
                                    onChange={handleIsSignificantChange}
                                >
                                    <MenuItem value={'true'}>{t('common.yes')}</MenuItem>
                                    <MenuItem value={'false'}>{t('common.no')}</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={getRiskDescriptionLabel()}
                                    disabled={!canChangeHazard}
                                    value={riskDescription}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.riskDescription !== newValue) {
                                            setChanges({
                                                ...changes,
                                                'RiskDescription': newValue,
                                            });
                                        } else {
                                            removeChange('RiskDescription');
                                        }
                                        setRiskDescription(newValue);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('hazardPage.field.complianceObligation')}
                                    disabled={!canChangeHazard}
                                    value={obligation}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.hzObligation !== newValue) {
                                            setChanges({
                                                ...changes,
                                                'Obligation': newValue,
                                            });
                                        } else {
                                            removeChange('Obligation');
                                        }
                                        setObligation(newValue);
                                    }}
                                />
                            </Grid>
                        </Section>

                        <Section title={t('hazardPage.section.riskScoring')}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('hazardPage.field.severity')}
                                    disabled={!canChangeHazard}
                                    value={severity.svName}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.hzSeverity !== severities.find(sev => sev.svName === newValue).svId) {
                                            setChanges({
                                                ...changes,
                                                'Severity': newValue,
                                            });
                                        } else {
                                            removeChange('Severity');
                                        }
                                        const sevValue = severities.find(sev => sev.svName === newValue);
                                        setSeverity({
                                            svId: sevValue?.svId,
                                            svName: sevValue.svName,
                                            svFactor: sevValue.svFactor,
                                        });

                                        if (sevValue) {
                                            calculateRiskScores(sevValue.svFactor, likelihood.lkFactor, controls);
                                        }
                                    }}
                                >
                                    {severities.map((sev) => (
                                        <MenuItem key={sev.svId} value={sev.svName}>
                                            {sev.localizedName ?? sev.svName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label={t('hazardPage.field.likelihood')}
                                    disabled={!canChangeHazard}
                                    value={likelihood.lkName}
                                    onChange={(event) => {
                                        const newValue = event.target.value;
                                        if(currentHazard?.hzLikelihood !== likelihoods.find(x => x.lkName === newValue).lkId) {
                                            setChanges({
                                                ...changes,
                                                'Likelihood': newValue,
                                            });
                                        } else {
                                            removeChange('Likelihood');
                                        }
                                        const likelihoodValue = likelihoods.find(likely => likely.lkName === newValue);

                                        setLikelihood({
                                            lkId: likelihoodValue?.lkId,
                                            lkName: likelihoodValue.lkName,
                                            lkFactor: likelihoodValue?.lkFactor,
                                        });

                                        if (likelihoodValue) {
                                            calculateRiskScores(severity.svFactor, likelihoodValue.lkFactor, controls);
                                        }
                                    }}
                                >
                                    {likelihoods.map((likely) => (
                                        <MenuItem key={likely.lkId} value={likely.lkName}>
                                            {likely.localizedName ?? likely.lkName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {t('fieldName.riskScoreUncontrolled')}
                                </Typography>
                                <InputBase
                                    readOnly
                                    fullWidth
                                    value={getRiskScoreValue(riskScoreUncontrolled.riskScore).text}
                                    sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1.25,
                                        fontSize: 16,
                                        backgroundColor: getRiskScoreValue(riskScoreUncontrolled.riskScore).color,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {t('fieldName.riskScoreControlled')}
                                </Typography>
                                <InputBase
                                    readOnly
                                    fullWidth
                                    value={getRiskScoreValue(riskScoreControlled.riskScore).text}
                                    sx={{
                                        border: '1px solid #ccc',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1.25,
                                        fontSize: 16,
                                        backgroundColor: getRiskScoreValue(riskScoreControlled.riskScore).color,
                                    }}
                                />
                            </Grid>
                        </Section>

                        <Section title={t('hazardPage.section.notes')}>
                            <Grid item xs={12}>
                                <CKEditor
                                    disabled={!canChangeHazard}
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: {
                                            items: [ 'heading',
                                                '|',
                                                'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                                '|',
                                                'bold', 'italic', 'link', 'bulletedList', 'numberedList',
                                                '|',
                                                'blockQuote', 'insertTable', 'mediaEmbed', 'codeBlock',
                                                '|',
                                                'todoList', 'outdent', 'indent', 'undo', 'redo' ]
                                        },
                                        plugins: [
                                            Bold, Essentials, Italic, Mention, Paragraph, Undo, Link, List, Heading, Indent, MediaEmbed, Table, FontFamily, FontSize, BlockQuote, AutoLink, LinkEditing, LinkUI
                                        ],
                                        link: {
                                            addTargetToExternalLinks: true,
                                        },
                                        mediaEmbed: {
                                            previewsInData: true,
                                        }
                                    }}
                                    data={notes ?? ''}
                                    onChange={(event, editor) => {
                                        const newValue = editor.getData();
                                        if(currentHazard?.hzNotes !== newValue) {
                                            setChanges({
                                                ...changes,
                                                'Notes': newValue,
                                            });
                                        } else {
                                            removeChange('Notes');
                                        }
                                        setNotes(newValue);
                                    }}
                                />
                            </Grid>
                        </Section>
                    </Grid>

                    {/* Right column — tabbed */}
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2 }}>
                            <Tabs
                                value={tabValue}
                                onChange={(_e, v) => setTabValue(v)}
                                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                                variant="fullWidth"
                            >
                                <Tab label={`${t('hazardPage.tabs.controls')} (${controls.length})`} />
                                <Tab label={`${t('hazardPage.tabs.files')} (${files.length})`} />
                                <Tab label={`${t('hazardPage.tabs.images')} (${images.length})`} />
                            </Tabs>

                            {/* Controls tab */}
                            {tabValue === 0 && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleAddControl}
                                            disabled={!isHazardCreated || !canChangeHazard}
                                        >
                                            {t('hazardPage.button.addControl')}
                                        </Button>
                                    </Box>
                                    <DataGridPro
                                        rows={controls}
                                        autoHeight
                                        localeText={{ noRowsLabel: t('hazardPage.empty.noControls') }}
                                        columns={controlsColumns}
                                        getRowHeight={() => 'auto'}
                                        getRowId={(row) => row.hcId}
                                    />
                                </Box>
                            )}

                            {/* Files tab */}
                            {tabValue === 1 && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={!isHazardCreated || !canChangeHazard}
                                            startIcon={<AttachFileOutlinedIcon/>}
                                            onClick={handleAddAttachment}
                                        >
                                            {t('hazardPage.button.addAttachment')}
                                        </Button>
                                    </Box>
                                    <DataGridPro
                                        rows={files}
                                        autoHeight
                                        localeText={{ noRowsLabel: t('hazardPage.empty.noFiles') }}
                                        columns={fileColumns}
                                        getRowHeight={() => 'auto'}
                                        getRowId={(row) => row.id}
                                    />
                                </Box>
                            )}

                            {/* Images tab */}
                            {tabValue === 2 && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25, flexWrap: 'wrap', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={!isHazardCreated || !canChangeHazard}
                                            startIcon={<AttachFileOutlinedIcon/>}
                                            onClick={handleAddAttachment}
                                        >
                                            {t('hazardPage.button.addAttachment')}
                                        </Button>
                                        {images.length > 0 && (
                                            <>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color="secondary"
                                                    onClick={handleAnalyzeImage}
                                                    disabled={analyzing}
                                                    startIcon={analyzing ? <CircularProgress size={16} color="inherit"/> : null}
                                                >
                                                    {analyzing ? t('hazardPage.button.analyzing') : t('hazardPage.button.analyzeRisks')}
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<DeleteOutlinedIcon/>}
                                                    color="error"
                                                    onClick={handleFileDelete}
                                                    disabled={!canChangeHazard}
                                                >
                                                    {t('hazardPage.button.deleteAttachment')}
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                    {images.length > 0 ? (
                                        <Box sx={{ position: 'relative' }}>
                                            <Image
                                                src={images.length > 0 ? images[currentImage].blobUrl : ''}
                                                alt=""
                                                style={{ width: '100%' }}
                                                onLoad={handleLoad}
                                            />
                                            {loading && <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <CircularProgress/>
                                            </Box>}
                                            {images.length > 1 && (
                                                <>
                                                    <Button onClick={handlePrev} sx={carouselArrowSx('left')}>
                                                        <ArrowBack/>
                                                    </Button>
                                                    <Button onClick={handleNext} sx={carouselArrowSx('right')}>
                                                        <ArrowForward/>
                                                    </Button>
                                                </>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box sx={{ bgcolor: '#bee5eb', color: 'black', p: 2 }}>
                                            <Typography variant="h6">{t('hazardPage.empty.noImages')}</Typography>
                                        </Box>
                                    )}
                                    {analysisError && (
                                        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setAnalysisError('')}>
                                            {analysisError}
                                        </Alert>
                                    )}
                                    {isHazardCreated && previousAnalysisResults.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {t('hazardPage.aiAnalysis.suggestedControls')}
                                            </Typography>
                                            {previousAnalysisResults.map((result) => (
                                                <Box key={result.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Chip
                                                                label={result.severity}
                                                                color={getSeverityColor(result.severity)}
                                                                size="small"
                                                                sx={{ mr: 1 }}
                                                            />
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                                {result.risk}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            {canChangeHazard && (
                                                                <>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ mr: 1 }}
                                                                        onClick={() => handleAddAsControl(result, result.id)}
                                                                    >
                                                                        {t('hazardPage.button.addAsControl')}
                                                                    </Button>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDismissResult(result.id)}
                                                                    >
                                                                        {t('common.dismiss')}
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <strong>{t('hazardPage.aiAnalysis.recommendation')}</strong> {result.recommendation}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('hazardPage.aiAnalysis.identified', { date: new Date(result.dateCreated).toLocaleDateString() })}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                {/* Risk Changes audit log */}
                <Box sx={{ width: '100%', mt: 2, px: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t('hazardPage.section.riskChanges')}
                    </Typography>
                    <Button sx={{ mb: 1.25 }} variant={'contained'} onClick={() => setShowRiskChanges(!showRiskChanges)}>
                        {showRiskChanges ? t('hazardPage.button.hideRiskChanges') : t('hazardPage.button.showRiskChanges')}
                    </Button>
                    {showRiskChanges && <DataGridPro
                        rows={hazardAudits}
                        autoHeight
                        localeText={{ noRowsLabel: t('hazardPage.empty.noChanges') }}
                        columns={hazardAuditColumns}
                        getRowHeight={() => 'auto'}
                        getRowId={(row) => row.id}
                    />}
                </Box>

                {/* Modals */}
                {isHazardCreated && (
                    <RiskControlModal
                        onClose={handleAddControlModalClose}
                        open={showAddControlModal}
                        controlTypes={controlTypes}
                        hazardId={hazardId}
                    />
                )}
                {controls.length > 0 && canChangeHazard && (
                    <ConfirmDeleteDialogHazard
                        keepMounted
                        open={showControlDeleteDialog}
                        onClose={handleDeleteControl}
                        deleteTitle={t('hazardPage.confirm.controlDeleteTitle')}
                        deleteLabel={t('hazardPage.confirm.controlDeleteLabel', { description: currentControl.hcDescription ?? '' })}
                    />
                )}
                {files.length > 0 && (
                    <ConfirmDeleteDialogHazard
                        keepMounted
                        open={showFileDeleteDialog && canChangeHazard}
                        onClose={onFileDeleteClick}
                        deleteTitle={t('hazardPage.confirm.attachmentDeleteTitle')}
                        deleteLabel={t('hazardPage.confirm.attachmentDeleteLabel', { filename: currentFile.filename ?? '' })}
                    />
                )}
                {images.length > 0 && (
                    <ConfirmDeleteDialogHazard
                        keepMounted
                        open={showImageDeleteDialog}
                        onClose={onImageDeleteClick}
                        deleteTitle={t('hazardPage.confirm.attachmentDeleteTitle')}
                        deleteLabel={t('hazardPage.confirm.attachmentDeleteLabel', { filename: images[currentImage].filename })}
                    />
                )}
                <FileUploadModal
                    onClose={handleFileUpload}
                    open={showUploadModal && canChangeHazard}
                    imageButton={true}
                />
                <Dialog
                    open={showAnalysisDialog}
                    onClose={() => setShowAnalysisDialog(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>{t('hazardPage.aiAnalysis.dialogTitle')}</DialogTitle>
                    <DialogContent dividers>
                        {analysisResult && (
                            <>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {analysisResult.summary}
                                </Alert>
                                {analysisResult.identifiedRisks?.length > 0 ? (
                                    analysisResult.identifiedRisks.map((risk, index) => (
                                        <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Chip
                                                        label={risk.severity}
                                                        color={getSeverityColor(risk.severity)}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    />
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                        {risk.risk}
                                                    </Typography>
                                                </Box>
                                                {canChangeHazard && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleAddAsControl(risk, null)}
                                                    >
                                                        {t('hazardPage.button.addAsControl')}
                                                    </Button>
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>{t('hazardPage.aiAnalysis.recommendation')}</strong> {risk.recommendation}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography>{t('hazardPage.aiAnalysis.noRisks')}</Typography>
                                )}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowAnalysisDialog(false)}>{t('common.close')}</Button>
                    </DialogActions>
                </Dialog>
            </form>
        </Box>
    );
};

export default Hazard;
